import {
    classifyIncident,
    shouldEmitIncidentToSink,
    type IncidentClassification,
} from '@/shared/observability/incident-policy';

export type IncidentDispatchDisposition = 'terminal' | 'rethrow';
export type ServerIncidentDispatchSink = 'prisma_log' | 'launchdarkly' | 'incident_sink';
export type BrowserIncidentDispatchSink = 'browser_launchdarkly' | 'browser_relay';
export type IncidentSenderResultReason =
    | 'delivered'
    | 'duplicate_suppressed'
    | 'not_configured'
    | 'unavailable'
    | 'failed'
    | 'skipped';

export type IncidentSenderResult = {
    delivered: boolean;
    reason: IncidentSenderResultReason;
    duplicateCount?: number;
};

export type IncidentDispatchAttempt<TSink extends string> = {
    sink: TSink;
    delivered: boolean;
    reason: IncidentSenderResultReason;
    duplicateCount: number;
    error?: string | null;
};

export type IncidentSender<TIncident, TSink extends string> = {
    sink: TSink;
    send: (incident: TIncident) => Promise<IncidentSenderResult>;
};

export type BaseDispatchedIncident = {
    title?: string | null;
    message: string;
    classification?: IncidentClassification | null;
    status?: number | null;
    errorCode?: string | null;
    errorCategory?: string | null;
    severity?: string | null;
    occurredAt?: string | null;
    fingerprint?: string | null;
    requestId?: string | null;
    correlationId?: string | null;
    operation?: string | null;
    clientId?: string | null;
    platform?: string | null;
    recommendedAction?: string | null;
    metadata?: Record<string, unknown> | null;
    disposition?: IncidentDispatchDisposition | null;
};

export type ServerDispatchedIncident = BaseDispatchedIncident & {
    scope: string;
    metadataText?: string | null;
    error?: Error | null;
    secureSessionId?: string | null;
    suppressionWindowMs?: number | null;
};

export type BrowserDispatchedIncident = BaseDispatchedIncident & {
    relayPath?: string | null;
    keepalive?: boolean;
};

const SERVER_DISPATCH_SINK_ORDER: ServerIncidentDispatchSink[] = ['prisma_log', 'launchdarkly', 'incident_sink'];
const BROWSER_DISPATCH_SINK_ORDER: BrowserIncidentDispatchSink[] = ['browser_launchdarkly', 'browser_relay'];

const isObjectRecord = (value: unknown): value is Record<string, unknown> => {
    return Object.prototype.toString.call(value) === '[object Object]';
};

const readOptionalNumber = (value: unknown): number | null => {
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
};

export const toDispatchedIncidentMetadataRecord = (value: unknown): Record<string, unknown> | null => {
    if (!value || !isObjectRecord(value)) {
        return null;
    }

    return value;
};

export const resolveIncidentDispatchClassification = (
    incident: Pick<BaseDispatchedIncident, 'classification' | 'errorCategory' | 'status' | 'errorCode' | 'metadata'> & {
        error?: unknown;
    },
): IncidentClassification => {
    const metadataRecord = toDispatchedIncidentMetadataRecord(incident.metadata);

    return classifyIncident({
        classification: incident.classification,
        category: incident.errorCategory,
        status: incident.status ?? readOptionalNumber(metadataRecord?.status),
        code: incident.errorCode,
        error: metadataRecord ?? incident.error,
    });
};

export const shouldDispatchServerIncidentToSink = ({
    classification,
    disposition = 'terminal',
    sink,
}: {
    classification: IncidentClassification;
    disposition?: IncidentDispatchDisposition | null;
    sink: ServerIncidentDispatchSink;
}): boolean => {
    if (sink === 'launchdarkly') {
        return disposition === 'terminal' && shouldEmitIncidentToSink({ classification, sink: 'launchdarkly' });
    }

    if (sink === 'prisma_log') {
        return shouldEmitIncidentToSink({ classification, sink: 'durable_log' });
    }

    return shouldEmitIncidentToSink({ classification, sink: 'incident_sink' });
};

export const shouldDispatchBrowserIncidentToSink = ({
    classification,
    disposition = 'terminal',
    sink,
}: {
    classification: IncidentClassification;
    disposition?: IncidentDispatchDisposition | null;
    sink: BrowserIncidentDispatchSink;
}): boolean => {
    if (sink === 'browser_launchdarkly') {
        return disposition === 'terminal' && shouldEmitIncidentToSink({ classification, sink: 'launchdarkly' });
    }

    return shouldEmitIncidentToSink({ classification, sink: 'incident_sink' });
};

export const resolveServerIncidentDispatchSinks = ({
    classification,
    disposition = 'terminal',
}: {
    classification: IncidentClassification;
    disposition?: IncidentDispatchDisposition | null;
}): ServerIncidentDispatchSink[] => {
    return SERVER_DISPATCH_SINK_ORDER.filter((sink) =>
        shouldDispatchServerIncidentToSink({
            classification,
            disposition,
            sink,
        }),
    );
};

export const resolveBrowserIncidentDispatchSinks = ({
    classification,
    disposition = 'terminal',
}: {
    classification: IncidentClassification;
    disposition?: IncidentDispatchDisposition | null;
}): BrowserIncidentDispatchSink[] => {
    return BROWSER_DISPATCH_SINK_ORDER.filter((sink) =>
        shouldDispatchBrowserIncidentToSink({
            classification,
            disposition,
            sink,
        }),
    );
};
